import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '@mui/material';
import { useGoogleLogin } from '@react-oauth/google';
import { isAxiosError } from 'axios';
import IosShareOutlinedIcon from '@mui/icons-material/IosShareOutlined';
import ShareIconGif from 'assets/images/shareingoogledoc.gif';
import { showErrorMessages } from 'helpers/error';
import { reqOAuthGoogle, reqShareDocs } from 'api/requests/main';
import { formatHtmlForGoogleDocs } from 'helpers/functions';
import { ShareSuccessModal } from './ShareSuccessModal';

type ShareButtonProps = {
    title: string;
    content: string;
};

export const ShareButton = ({ title, content }: ShareButtonProps) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);

    const sendAuthCodeToBackend = async (code: string) => {
        try {
            await reqOAuthGoogle(code);
            await onShareDocs();
        } catch (error) {
            console.error('[ShareButton] Error', error);
            showErrorMessages(t('web_app.share_button.oauth_failed'));
            setIsLoading(false);
        }
    };

    const login = useGoogleLogin({
        flow: 'auth-code',
        scope: 'https://www.googleapis.com/auth/drive.file',
        onSuccess: (codeResponse) => {
            sendAuthCodeToBackend(codeResponse.code);
        },
        onError: (errorResponse) => {
            console.error('[ShareButton] Google login error:', errorResponse);
            showErrorMessages(t('web_app.share_button.auth_failed'));
            setIsLoading(false);
        },
    });

    const onShareDocs = async () => {
        setIsLoading(true);
        setDocumentUrl(null);

        try {
            const formatted = formatHtmlForGoogleDocs(content);
            const response = await reqShareDocs(title, formatted);
            setDocumentUrl(response?.data.google_docs_url);
            setShowModal(true);
            setIsLoading(false);
        } catch (error: unknown) {
            showErrorMessages(error);
            setIsLoading(false);
            if (isAxiosError(error)) {
                if (error.response && error.response.status == 403) {
                    login();
                    console.log(error.status);
                } else {
                    console.error('[ShareButton] An API error occurred:', error.message);
                    showErrorMessages(t('web_app.share_button.create_failed'));
                    setIsLoading(false);
                }
            } else {
                console.error('[ShareButton] unexpected error:', error);
                showErrorMessages(t('web_app.share_button.unexpected_error'));
                setIsLoading(false);
            }
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setDocumentUrl(null);
    };

    return (
        <>
            <Tooltip placement="top" title={t('web_app.tooltips.google_docs_btn')}>
                <span>
                    <button className="action-btn" onClick={onShareDocs} disabled={isLoading}>
                        {!isLoading ? (
                            <IosShareOutlinedIcon />
                        ) : (
                            <img src={ShareIconGif} alt="share-icon-loading" />
                        )}
                    </button>
                </span>
            </Tooltip>
            <ShareSuccessModal
                open={showModal}
                documentUrl={documentUrl}
                onClose={handleModalClose}
            />
        </>
    );
};
